/**
 * @license Siftrock
 * (c) 2014-2018 Siftrock, Inc. http://siftrock.com
 * Unauthorized copying of this file, via any medium is strictly prohibited
 */

import { get } from 'lodash/object';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { UserService } from '../services/user.service';
import { CacheService } from '../global/ng2-cache/services/cache.service';
import { SessionService } from '../services/session2.service';
import { ErrorService } from '../services/error.service';
import { GlobalMessageService } from '../services/global-message.service';

import { FormValidators } from '../global/form-validators';
import { ServiceWorkerService } from 'app/services/sw.service';

@Component({
  selector: 'app-settings',
  templateUrl: './settings.component.html'
  // host: { '[@routerTransition]': 'true' },
  // animations: Animations.page
})
export class SettingsComponent implements OnInit {
  session: any;

  profile: any = {};

  passwordForm: FormGroup;

  accountName: string = '';

  loadingPage: boolean = true;

  notifications: any = {};

  weeklyActivitySummaryEnabled: boolean = false;

  webPushIsSupported: boolean = false;

  constructor(fb: FormBuilder,
              private userService: UserService,
              private cacheService: CacheService,
              private sessionService: SessionService,
              private errorService: ErrorService,
              private globalMessageService: GlobalMessageService,
              private serviceWorkerService: ServiceWorkerService) {
    this.passwordForm = fb.group({
      pw1: ['', Validators.compose([Validators.required, Validators.minLength(8), FormValidators.passwordMatchValidator, FormValidators.passwordRequirementValidator])],
      pw2: ['', Validators.compose([Validators.required, Validators.minLength(8), FormValidators.passwordMatchValidator])]
    });

    this.webPushIsSupported = serviceWorkerService.isAvailable();

    // so we can trigger match validator
    this.passwordForm.controls['pw2'].valueChanges.subscribe(data => {
      this.passwordForm.controls['pw1'].updateValueAndValidity();
    });
  }

  ngOnInit() {
    this.session = this.cacheService.get('session');
    if (!this.session) {
      this.sessionService.getSession(true).subscribe(
        (session: any) => {
          this.session = session;
          this.initView();
        }
      )
    }
    else {
      this.initView();
    }
  }

  submitForm(): void {
    this.userService.saveProfile(this.profile.name).subscribe(
    () => {
      this.globalMessageService.message('Your user profile was saved');
    },
    (err) => {
      this.globalMessageService.error();
      this.errorService.log(err, 'settings.component', 'submitForm');
    });
  }

  cancelForm(): void {
    this.userService.getProfile().subscribe(
    (result: any) => {
      this.profile = result;
      this.globalMessageService.message('Changes to profile were discarded');
    },
    (err) => {
      this.globalMessageService.error();
      this.errorService.log(err, 'settings.component', 'cancelForm');
    });
  }

  submitPasswordForm(): void {
    this.userService.changePassword(this.passwordForm.controls['pw1'].value).subscribe(
    () => {
      this.globalMessageService.message('Your password was changed');
      this.passwordForm.controls['pw1'].setValue('');
      this.passwordForm.controls['pw2'].setValue('');
    },
    (err) => {
      this.globalMessageService.error();
      this.errorService.log(err, 'settings.component', 'submitPasswordForm');
    });
  }

  cancelPasswordForm(): void {
    this.passwordForm.controls['pw1'].setValue('');
    this.passwordForm.controls['pw2'].setValue('');
  }

  toggleNotifications(feature: string, message: string): void {
    this.notifications[feature] = !this.notifications[feature];
    if (feature.indexOf('web_push') > -1 && this.notifications[feature]) {
      this.maybeRequestPush();
    }
    this.updateNotifications(message);
  }

  toggleWeeklyActivitySummary() {
    const notificationConfig = this.notifications.weekly_account_summary;
    this.weeklyActivitySummaryEnabled = !this.weeklyActivitySummaryEnabled;
    if (!notificationConfig) {
      this.notifications.weekly_account_summary = { enabled: true };
    } else {
      this.notifications.weekly_account_summary.enabled = !this.notifications.weekly_account_summary.enabled;
    }
    this.updateNotifications('Weekly activity summary');
  }

  private updateNotifications(message: string) {
    this.userService.updateNotifications(this.notifications).subscribe(
      () => {
        this.globalMessageService.message(`${message} settings were updated`);
      },
      (err) => {
        this.globalMessageService.error();
        this.errorService.log(err, 'settings.component', 'toggleNotifications');
      }
    );
  }

  private async maybeRequestPush() {
    const WindowNotification = (window as any).Notification;
    if (WindowNotification.permission === 'default') {
      const permission = await WindowNotification.requestPermission();
      if (permission === 'granted') {
        await this.serviceWorkerService.subscribe();
      }
    }
  }

  private initView() {
    this.userService.getProfile().subscribe(
    (result: any) => {
      this.profile = result;
      this.loadingPage = false;
      this.notifications = this.profile.notifications;
      this.weeklyActivitySummaryEnabled = get(this.notifications, 'weekly_account_summary.enabled', false);
    },
    (err) => {
      this.globalMessageService.error();
      this.errorService.log(err, 'settings.component', 'ngOnInit');
    });
  }
}



// WEBPACK FOOTER //
// ./client/app/settings/settings.component.ts